import { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

// direction: 'horizontal' | 'vertical'
// thumbSize: thumb 길이 (px, 기본 100)
// thumbColor: thumb 색상 (기본 theme.subKey)
// trackColor: track 색상 (기본 theme.buttonFill)
// scrollRef: 스크롤 대상 요소의 ref
// showTrackAlways: 스크롤 불가능해도 트랙 표시 여부 (기본 true)
export default function CustomScrollbar(props) {
  const {
    direction = 'horizontal',
    thumbSize = 100,
    thumbColor,
    trackColor,
    scrollRef,
    showTrackAlways = true,
  } = props;

  const isHorizontal = direction === 'horizontal';

  const [thumbPosition, setThumbPosition] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef(0);
  const dragStartScroll = useRef(0);

  // thumb DOM 직접 위치 업데이트 (리렌더 없이) //
  const updateThumbDirect = useCallback((position) => {
    const thumb = thumbRef.current;
    if (!thumb) return;
    const prop = isHorizontal ? 'left' : 'top';
    thumb.style[prop] = position + 'px';
  }, [isHorizontal]);

  // 스크롤 thumb 위치 계산 //
  const updateScrollbar = useCallback(() => {
    const el = scrollRef?.current;
    if (!el) return;

    const totalSize = isHorizontal ? el.scrollWidth : el.scrollHeight;
    const viewSize = isHorizontal ? el.clientWidth : el.clientHeight;
    const scrollPos = isHorizontal ? el.scrollLeft : el.scrollTop;
    const canScroll = totalSize > viewSize;

    setIsScrollable(canScroll);

    if (canScroll) {
      const scrollRatio = scrollPos / (totalSize - viewSize);
      const trackSize = isHorizontal
        ? (trackRef.current?.clientWidth || viewSize)
        : (trackRef.current?.clientHeight || viewSize);

      const newPosition = scrollRatio * (trackSize - thumbSize);

      if (isDragging.current) {
        updateThumbDirect(newPosition);
      } else {
        setThumbPosition(newPosition);
      }
    }
  }, [scrollRef, isHorizontal, thumbSize, updateThumbDirect]);

  // 스크롤 이벤트 리스너 등록 //
  useEffect(() => {
    const timer = setTimeout(() => updateScrollbar(), 100);

    const el = scrollRef?.current;
    if (!el) return () => clearTimeout(timer);

    el.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', updateScrollbar);

    // 콘텐츠 크기 변화 감지 //
    const resizeObserver = new ResizeObserver(() => updateScrollbar());
    resizeObserver.observe(el);
    if (el.firstElementChild) resizeObserver.observe(el.firstElementChild);

    return () => {
      clearTimeout(timer);
      el.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', updateScrollbar);
      resizeObserver.disconnect();
    };
  }, [scrollRef, updateScrollbar]);

  // thumb 드래그로 스크롤 조작 //
  const onThumbMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStart.current = isHorizontal ? e.clientX : e.clientY;

    const el = scrollRef?.current;

    dragStartScroll.current = isHorizontal ? (el?.scrollLeft || 0) : (el?.scrollTop || 0);

    const onMouseMove = (e) => {
      if (!isDragging.current || !scrollRef?.current || !trackRef.current) return;

      const el = scrollRef.current;
      const trackSize = isHorizontal ? trackRef.current.clientWidth : trackRef.current.clientHeight;
      const totalSize = isHorizontal ? el.scrollWidth : el.scrollHeight;
      const viewSize = isHorizontal ? el.clientWidth : el.clientHeight;
      const maxScroll = totalSize - viewSize;
      const maxThumbPos = trackSize - thumbSize;

      const delta = (isHorizontal ? e.clientX : e.clientY) - dragStart.current;
      const scrollDelta = (delta / maxThumbPos) * maxScroll;

      if (isHorizontal) el.scrollLeft = dragStartScroll.current + scrollDelta;
      else el.scrollTop = dragStartScroll.current + scrollDelta;
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [scrollRef, isHorizontal, thumbSize]);

  if (!showTrackAlways && !isScrollable) return null;

  return (
    <Track
      ref={trackRef}
      $direction={direction}
      $trackColor={trackColor}>
      {
        isScrollable &&
        <Thumb
          ref={thumbRef}
          $direction={direction}
          $thumbSize={thumbSize}
          $thumbPosition={thumbPosition}
          $thumbColor={thumbColor}
          onMouseDown={onThumbMouseDown}
        />
      }
    </Track>
  );
}

const Track = styled.div`
  position: relative;

  ${props => props.$direction === 'horizontal' ? `
    width: 100%;
    height: 5px;
    margin-top: 10px;
  ` : `
    width: 5px;
    height: 100%;
    margin-left: 10px;
  `}

  border-radius: 300px;
  background-color: ${props => props.$trackColor || props.theme.buttonFill};

  flex-shrink: 0;
`;

const Thumb = styled.div`
  position: absolute;

  ${props => props.$direction === 'horizontal' ? `
    top: 0;
    left: ${props.$thumbPosition}px;
    width: ${props.$thumbSize}px;
    height: 5px;
  ` : `
    left: 0;
    top: ${props.$thumbPosition}px;
    width: 5px;
    height: ${props.$thumbSize}px;
  `}

  border-radius: 50px;
  background-color: ${props => props.$thumbColor || props.theme.subKey};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;
